/**
 * Pricing Rules Context
 * 
 * Admin-only context for managing minimum lead pricing rules.
 * Controls floor prices by vertical, lead type, and state.
 * 
 * In-memory only — no persistence, no backend.
 */

import React, {
  createContext,
  useState,
  useCallback,
  useContext,
  ReactNode,
} from "react";
import { normalizeLeadTypeId } from "@/lib/leads/leadTypeIds";

// --- Type Definitions ---

export interface PricingRule {
  id: string;
  vertical: "life_insurance" | "mortgage";
  leadTypeId: string;
  leadTypeName: string;
  state: string; // "ALL" or specific state (e.g., "CA")
  basePrice: number; // floor price
  active: boolean;
}

interface PricingRulesState {
  rules: PricingRule[];
}

interface PricingRulesActions {
  /** Add a new pricing rule */
  addPricingRule: (rule: Omit<PricingRule, "id">) => void;

  /** Update an existing pricing rule */
  updatePricingRule: (id: string, updates: Partial<PricingRule>) => void;

  /** Toggle active status of a pricing rule */
  togglePricingRuleActive: (id: string) => void;

  /** Get the applicable pricing rule for a lead (state-specific overrides ALL) */
  getPricingRuleForLead: (leadTypeId: string, state: string) => PricingRule | null;

  /** Get all rules (read-only) */
  getAllPricingRules: () => PricingRule[];

  /** Get rules filtered by vertical */
  getRulesByVertical: (vertical: "life_insurance" | "mortgage") => PricingRule[];

  /** Delete a pricing rule */
  deletePricingRule: (id: string) => void;
}

// --- Initial Mock Data ---

const initialPricingRules: PricingRule[] = [
  // Life Insurance - Final Expense
  {
    id: "pr-fex-all",
    vertical: "life_insurance",
    leadTypeId: "life_fex",
    leadTypeName: "Final Expense",
    state: "ALL",
    basePrice: 25,
    active: true,
  },
  {
    id: "pr-fex-ca",
    vertical: "life_insurance",
    leadTypeId: "life_fex",
    leadTypeName: "Final Expense",
    state: "CA",
    basePrice: 32,
    active: true,
  },
  {
    id: "pr-fex-ny",
    vertical: "life_insurance",
    leadTypeId: "life_fex",
    leadTypeName: "Final Expense",
    state: "NY",
    basePrice: 35,
    active: true,
  },
  {
    id: "pr-fex-tx",
    vertical: "life_insurance",
    leadTypeId: "life_fex",
    leadTypeName: "Final Expense",
    state: "TX",
    basePrice: 28,
    active: true,
  },
  // Life Insurance - IUL
  {
    id: "pr-iul-all",
    vertical: "life_insurance",
    leadTypeId: "life_iul",
    leadTypeName: "IUL",
    state: "ALL",
    basePrice: 45,
    active: true,
  },
  {
    id: "pr-iul-ca",
    vertical: "life_insurance",
    leadTypeId: "life_iul",
    leadTypeName: "IUL",
    state: "CA",
    basePrice: 55,
    active: true,
  },
  // Life Insurance - Mortgage Protection
  {
    id: "pr-mp-all",
    vertical: "life_insurance",
    leadTypeId: "life_mp",
    leadTypeName: "Mortgage Protection",
    state: "ALL",
    basePrice: 35,
    active: true,
  },
  {
    id: "pr-mp-fl",
    vertical: "life_insurance",
    leadTypeId: "life_mp",
    leadTypeName: "Mortgage Protection",
    state: "FL",
    basePrice: 42,
    active: true,
  },
  // Mortgage - Purchase
  {
    id: "pr-purchase-all",
    vertical: "mortgage",
    leadTypeId: "mortgage_purchase",
    leadTypeName: "Purchase",
    state: "ALL",
    basePrice: 55,
    active: true,
  },
  {
    id: "pr-purchase-ca",
    vertical: "mortgage",
    leadTypeId: "mortgage_purchase",
    leadTypeName: "Purchase",
    state: "CA",
    basePrice: 70,
    active: true,
  },
  // Mortgage - Refinance
  {
    id: "pr-refi-all",
    vertical: "mortgage",
    leadTypeId: "mortgage_refi",
    leadTypeName: "Refinance",
    state: "ALL",
    basePrice: 40,
    active: true,
  },
  // Mortgage - HELOC
  {
    id: "pr-heloc-all",
    vertical: "mortgage",
    leadTypeId: "mortgage_heloc",
    leadTypeName: "HELOC",
    state: "ALL",
    basePrice: 35,
    active: true,
  },
  // Mortgage - Reverse
  {
    id: "pr-reverse-all",
    vertical: "mortgage",
    leadTypeId: "mortgage_reverse",
    leadTypeName: "Reverse",
    state: "ALL",
    basePrice: 50,
    active: true,
  },
  {
    id: "pr-reverse-az",
    vertical: "mortgage",
    leadTypeId: "mortgage_reverse",
    leadTypeName: "Reverse",
    state: "AZ",
    basePrice: 60,
    active: true,
  },
  // Mortgage - Cash-Out
  {
    id: "pr-cashout-all",
    vertical: "mortgage",
    leadTypeId: "mortgage_refi",
    leadTypeName: "Cash-Out",
    state: "ALL",
    basePrice: 45,
    active: true,
  },
];

// --- Context ---

const PricingRulesContext = createContext<{
  state: PricingRulesState;
  actions: PricingRulesActions;
} | undefined>(undefined);

// --- Provider ---

export const PricingRulesProvider = ({ children }: { children: ReactNode }) => {
  const [rules, setRules] = useState<PricingRule[]>(initialPricingRules);

  const addPricingRule = useCallback((rule: Omit<PricingRule, "id">) => {
    const newRule: PricingRule = {
      ...rule,
      id: `pr-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };
    setRules((prev) => [...prev, newRule]);
  }, []);

  const updatePricingRule = useCallback((id: string, updates: Partial<PricingRule>) => {
    setRules((prev) =>
      prev.map((rule) =>
        rule.id === id ? { ...rule, ...updates } : rule
      )
    );
  }, []);

  const togglePricingRuleActive = useCallback((id: string) => {
    setRules((prev) =>
      prev.map((rule) =>
        rule.id === id ? { ...rule, active: !rule.active } : rule
      )
    );
  }, []);

  const deletePricingRule = useCallback((id: string) => {
    setRules((prev) => prev.filter((rule) => rule.id !== id));
  }, []);

  const getPricingRuleForLead = useCallback(
    (leadTypeId: string, state: string): PricingRule | null => {
      const canonicalLeadTypeId = normalizeLeadTypeId(leadTypeId);
      // Find active rules for this lead type
      const applicableRules = rules.filter(
        (rule) =>
          normalizeLeadTypeId(rule.leadTypeId) === canonicalLeadTypeId && rule.active
      );

      if (applicableRules.length === 0) return null;

      // State-specific rule takes priority over "ALL"
      const stateSpecificRule = applicableRules.find(
        (rule) => rule.state === state
      );
      
      if (stateSpecificRule) return stateSpecificRule;

      // Fallback to "ALL" rule
      const allStatesRule = applicableRules.find(
        (rule) => rule.state === "ALL"
      );

      return allStatesRule || null;
    },
    [rules]
  );

  const getAllPricingRules = useCallback(() => {
    return [...rules];
  }, [rules]);

  const getRulesByVertical = useCallback(
    (vertical: "life_insurance" | "mortgage") => {
      return rules.filter((rule) => rule.vertical === vertical);
    },
    [rules]
  );

  const actions: PricingRulesActions = {
    addPricingRule,
    updatePricingRule,
    togglePricingRuleActive,
    getPricingRuleForLead,
    getAllPricingRules,
    getRulesByVertical,
    deletePricingRule,
  };

  return (
    <PricingRulesContext.Provider value={{ state: { rules }, actions }}>
      {children}
    </PricingRulesContext.Provider>
  );
};

// --- Hook ---

export const usePricingRules = () => {
  const context = useContext(PricingRulesContext);
  if (context === undefined) {
    throw new Error("usePricingRules must be used within a PricingRulesProvider");
  }
  return context;
};

export default PricingRulesContext;
